import { StyleSheet, Text, View } from "react-native";
import { useTranslation } from "react-i18next";

import {
  estimateLabelKey,
  formatDistance,
  formatKcal,
  type CaloriesEstimate,
} from "@heraion/shared";

import { IconButton } from "../primitives/IconButton";
import { helpTextStyle } from "../primitives/helpTextStyle";
import { MetricBlock, metricValueTextStyle } from "../primitives/MetricBlock";
import { useDisclosure } from "../primitives/useDisclosure";
import type { UiTheme } from "../theme";
import { makeCardBoxStyle } from "../primitives/cardBoxStyle";

// HC-8: the workout energy readout: Metria's calorie estimate + effort (HR +
// %HRmax). ONE shared block for every surface it appears on (mobile logger
// footer, live during a workout + on edit reopen; the post-save card; the
// web-rnw session detail), mounted thin, never re-rolled per app
// ([[feedback_web_means_web_rnw]]).
//
// #290: the estimate is the only calorie figure, and it always says so beside
// the value («estimate», or «rough estimate» at low confidence). There is no
// source list to reveal any more; the (i) stays only to declare a live
// projection. Effort is FACTUAL (HR + %HRmax), never a zone interpretation
// (HC-8 exposes; the coach interprets), and it reaches here only for a session
// with a cardio bout (sessionEnergyReadout).
export type CaloriesEffortBlockProps = {
  theme: UiTheme;
  /** Metria's estimate; null when the session carries none (nothing to show). */
  estimate: CaloriesEstimate | null;
  avgHrBpm?: number;
  peakHrBpm?: number;
  /** %HRmax; the caller computes `percentHrMax(avgHrBpm, ageFromBucket(bucket))`. */
  hrMaxPct?: number | null;
  /** #156: total cardio distance in metres (from the readout); omitted = no block. */
  distanceM?: number;
  /** LG-46: the calorie figure is a LIVE projection: it counts the set the
   *  athlete is about to do, not just work already logged. When set, an (i)
   *  disclosure says so. Never set on a saved session (nothing is proposed). */
  projected?: boolean;
  /** Opt-in card wrapper (surface bg + border + radius + padding, matching
   *  this surface's sibling cards — WorkoutTotalsCard/WorkoutHeatMap in the
   *  logger footer) so the block stops floating with only a divider (DS-18).
   *  Default OFF: `<SessionSummaryView>` already renders this block inside
   *  its own card, so double-boxing there would nest a card in a card —
   *  only the logger footer (where it sits bare beside boxed siblings)
   *  opts in. */
  boxed?: boolean;
};

export function CaloriesEffortBlock({
  theme,
  estimate,
  avgHrBpm,
  peakHrBpm,
  hrMaxPct,
  distanceM,
  projected = false,
  boxed = false,
}: CaloriesEffortBlockProps) {
  const { t, i18n } = useTranslation();
  const styles = makeStyles(theme);
  const { expanded, toggle } = useDisclosure();

  const hasEffort =
    avgHrBpm !== undefined ||
    peakHrBpm !== undefined ||
    (hrMaxPct ?? null) !== null;

  const hasDistance = distanceM !== undefined && distanceM > 0;

  if (!estimate && !hasEffort && !hasDistance) return null;

  const content = (
    <>
      {estimate ? (
        <MetricBlock
          theme={theme}
          title={t("mobile.postSession.blockCalories", { defaultValue: "Calories" })}
        >
          <View style={styles.valueRow}>
            <Text style={styles.value}>{formatKcal(estimate.kcal)}</Text>
            <Text style={styles.estimateTag}>
              {t(estimateLabelKey(estimate.confidence))}
            </Text>
            {projected ? (
              <IconButton
                theme={theme}
                name="info"
                expanded={expanded}
                active={expanded}
                onPress={toggle}
                accessibilityLabel={t("mobile.energySource.pillLabel", {
                  defaultValue: "About this estimate",
                })}
              />
            ) : null}
          </View>
          {projected && expanded ? (
            <Text style={styles.projectionNote}>
              {t("mobile.energySource.projectionNote", {
                defaultValue:
                  "Includes the set you're about to do: a projection of the whole session, not work you've already logged.",
              })}
            </Text>
          ) : null}
        </MetricBlock>
      ) : null}

      {hasEffort ? (
        <MetricBlock
          theme={theme}
          title={t("mobile.postSession.blockEffort", { defaultValue: "Effort" })}
        >
          {avgHrBpm !== undefined || peakHrBpm !== undefined ? (
            <Text style={styles.value}>
              {t("mobile.postSession.effortHr", {
                defaultValue: "{{avg}} avg · {{peak}} peak bpm",
                avg: avgHrBpm ?? "—",
                peak: peakHrBpm ?? "—",
              })}
            </Text>
          ) : null}
          {(hrMaxPct ?? null) !== null ? (
            <Text style={styles.hrMax}>
              {t("mobile.postSession.effortHrMax", {
                defaultValue: "{{pct}}% HRmax",
                pct: hrMaxPct,
              })}
            </Text>
          ) : null}
        </MetricBlock>
      ) : null}

      {hasDistance ? (
        <MetricBlock
          theme={theme}
          title={t("mobile.postSession.blockDistance", { defaultValue: "Distance" })}
          value={formatDistance(distanceM ?? 0, i18n.language)}
        />
      ) : null}
    </>
  );

  return boxed ? <View style={styles.box}>{content}</View> : content;
}

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, typography: ty } = theme;
  return StyleSheet.create({
    // Opt-in card chrome (`boxed`). Comes from makeCardBoxStyle, the ONE box
    // literal WorkoutTotalsCard and WorkoutHeatMap now share too: writing it
    // by hand here would have been the third identical copy (DS-18 review).
    box: makeCardBoxStyle(theme),
    valueRow: {
      flexDirection: "row",
      flexWrap: "wrap",
      alignItems: "center",
      gap: s.s2,
    },
    // Shared metric-value look (ink · lg · 700 · tabular), rendered inline here
    // because the number shares its row with the «estimate» tag.
    value: metricValueTextStyle(theme),
    // #290: the «estimate» tag, the shared attenuated help-text recipe.
    estimateTag: helpTextStyle(theme),
    // LG-46: the projection caveat, quieter than the value (it explains), at
    // the foot of the (i) disclosure.
    projectionNote: { color: p.ink2, fontSize: ty.sizes.sm, paddingTop: s.s1 },
    hrMax: { color: p.ink2, fontSize: ty.sizes.sm },
  });
}
